import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';

const tableMap = {
  '/':             { table: 'leaderboard',             title: '🩷 Flutter Heart' },
  '/flutter':      { table: 'leaderboard',             title: '🩷 Flutter Heart' },
  '/cloud':        { table: 'cloud_leaderboard',        title: '☁️ Cloud Dodge'  },
  '/flutterrush':  { table: 'heartrush_leaderboard',   title: 'Flutter Rush 🏃'  },
  '/flutterhoop':    { table: 'flutterhoop_leaderboard',   title: '🌀 Flutter Hoop'   },
  '/heartcatcher':   { table: 'heartcatcher_leaderboard', title: '💝 Heart Catcher' },
  '/fluttercatch':   { table: 'fluttercatch_leaderboard', title: '🫧 Flutter Catch'  },
};

export function Leagues({ user, gameId = '/', onBack }) {
  const [view, setView] = useState('hub');
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leagueScores, setLeagueScores] = useState([]);
  const [period, setPeriod] = useState('alltime');
  const [userRank, setUserRank] = useState(null);

  const [createName, setCreateName] = useState('');
  const [newLeague, setNewLeague] = useState(null);
  const [createError, setCreateError] = useState('');

  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joinSuccess, setJoinSuccess] = useState('');

  const { table, title } = tableMap[gameId] || tableMap['/'];

  const fetchLeagues = useCallback(async () => {
    const { data, error } = await supabase
      .from('league_members')
      .select('leagues(id, name, invite_code, created_by, created_at)')
      .eq('user_id', user.id);
    if (!error && data) {
      setLeagues(data.map((m) => m.leagues).filter(Boolean));
    }
  }, [user.id]);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  useEffect(() => {
    if (!selectedLeague) return;

    const fetchScores = async () => {
      const { data: members } = await supabase
        .from('league_members')
        .select('user_id')
        .eq('league_id', selectedLeague.id);

      if (!members || members.length === 0) {
        setLeagueScores([]);
        setUserRank(null);
        return;
      }

      const memberIds = members.map((m) => m.user_id);

      let query = supabase
        .from(table)
        .select('*')
        .in('user_id', memberIds)
        .order('score', { ascending: false })
        .limit(10);

      if (period === 'weekly') {
        const since = new Date();
        since.setDate(since.getDate() - 7);
        query = query.gte('created_at', since.toISOString());
      } else if (period === 'monthly') {
        const since = new Date();
        since.setMonth(since.getMonth() - 1);
        query = query.gte('created_at', since.toISOString());
      }

      const { data, error } = await query;
      if (error) { console.error(error); return; }
      setLeagueScores(data);

      const userScores = data.filter((e) => e.user_id === user.id);
      if (userScores.length > 0) {
        const userBest = Math.max(...userScores.map((e) => e.score));
        const rank = data.findIndex((e) => e.score === userBest) + 1;
        setUserRank(rank);
      } else {
        setUserRank(null);
      }
    };

    fetchScores();
  }, [selectedLeague, table, period, user.id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!createName.trim()) return;

    const inviteCode = Math.random().toString(36).slice(2, 8).toUpperCase();

    const { data: league, error } = await supabase
      .from('leagues')
      .insert([{ name: createName.trim(), invite_code: inviteCode, created_by: user.id }])
      .select()
      .single();

    if (error) {
      console.error('League create error:', error);
      setCreateError(error.message || 'Something went wrong. Please try again.');
      return;
    }

    await supabase.from('league_members').insert([{ league_id: league.id, user_id: user.id }]);
    setNewLeague(league);
    setCreateName('');
    await fetchLeagues();
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setJoinError('');
    setJoinSuccess('');

    const { data: league, error } = await supabase
      .from('leagues')
      .select('*')
      .eq('invite_code', joinCode.trim().toUpperCase())
      .single();

    if (error || !league) {
      setJoinError('Invalid invite code. Check with your friend and try again.');
      return;
    }

    const { data: existing } = await supabase
      .from('league_members')
      .select('id')
      .eq('league_id', league.id)
      .eq('user_id', user.id)
      .single();

    if (existing) {
      setJoinError("You're already in this league!");
      return;
    }

    await supabase.from('league_members').insert([{ league_id: league.id, user_id: user.id }]);
    setJoinSuccess(`Joined "${league.name}"!`);
    setJoinCode('');
    await fetchLeagues();
  };

  // ── Hub ──────────────────────────────────────────────────
  if (view === 'hub') {
    return (
      <div className="leagues-container">
        <div className="leagues-header">
          <button className="btn" onClick={onBack}>← Back</button>
          <h1>🏅 Leagues</h1>
          <div style={{ width: 80 }} />
        </div>

        <div className="leagues-actions">
          <button className="btn btn-play" onClick={() => { setNewLeague(null); setCreateError(''); setView('create'); }}>
            + Create League
          </button>
          <button className="btn btn-play" onClick={() => { setJoinError(''); setJoinSuccess(''); setJoinCode(''); setView('join'); }}>
            Join League
          </button>
        </div>

        {leagues.length === 0 ? (
          <p className="leagues-empty">
            You haven't joined any leagues yet.<br />
            Create one or ask a friend for their invite code.
          </p>
        ) : (
          <div className="leagues-list">
            {leagues.map((league) => (
              <button
                key={league.id}
                className="league-card"
                onClick={() => { setSelectedLeague(league); setPeriod('alltime'); setLeagueScores([]); setView('detail'); }}
              >
                <div className="league-card-body">
                  <div className="league-card-name">{league.name}</div>
                  <div className="league-card-code">Code: {league.invite_code}</div>
                </div>
                <span className="league-card-arrow">→</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Create ───────────────────────────────────────────────
  if (view === 'create') {
    return (
      <div className="leagues-container">
        <div className="leagues-header">
          <button className="btn" onClick={() => setView('hub')}>← Back</button>
          <h1>Create League</h1>
          <div style={{ width: 80 }} />
        </div>

        {newLeague ? (
          <div className="league-created">
            <div className="league-created-check">✓</div>
            <p className="league-created-title">"{newLeague.name}" is live!</p>
            <p className="league-created-label">Share this invite code with your friends:</p>
            <div className="invite-code-box">{newLeague.invite_code}</div>
            <button className="btn btn-play" onClick={() => { setNewLeague(null); setView('hub'); }}>Done</button>
          </div>
        ) : (
          <form className="leagues-form" onSubmit={handleCreate}>
            <label className="leagues-label">League Name</label>
            <input
              className="leagues-input"
              type="text"
              placeholder="e.g. The Dream Team"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              maxLength={40}
              autoFocus
            />
            {createError && <p className="leagues-error">{createError}</p>}
            <button className="btn btn-play" type="submit" disabled={!createName.trim()}>
              Create
            </button>
          </form>
        )}
      </div>
    );
  }

  // ── Join ─────────────────────────────────────────────────
  if (view === 'join') {
    return (
      <div className="leagues-container">
        <div className="leagues-header">
          <button className="btn" onClick={() => setView('hub')}>← Back</button>
          <h1>Join League</h1>
          <div style={{ width: 80 }} />
        </div>

        <form className="leagues-form" onSubmit={handleJoin}>
          <label className="leagues-label">Invite Code</label>
          <input
            className="leagues-input leagues-input--code"
            type="text"
            placeholder="ABC123"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            maxLength={6}
            autoFocus
          />
          {joinError && <p className="leagues-error">{joinError}</p>}
          {joinSuccess && <p className="leagues-success">{joinSuccess}</p>}
          <button className="btn btn-play" type="submit" disabled={joinCode.length < 6}>
            Join
          </button>
        </form>
      </div>
    );
  }

  // ── Detail ───────────────────────────────────────────────
  if (view === 'detail' && selectedLeague) {
    return (
      <div className="leaderboard-container">
        <button className="btn" style={{ alignSelf: 'flex-start' }} onClick={() => setView('hub')}>
          ← Leagues
        </button>
        <h1>{title}</h1>
        <div className="league-detail-header">
          <span className="league-detail-name">{selectedLeague.name}</span>
          <span className="league-detail-code">Code: <strong>{selectedLeague.invite_code}</strong></span>
        </div>

        <div className="leaderboard-tabs">
          {['weekly', 'monthly', 'alltime'].map((p) => (
            <button
              key={p}
              className={`tab-btn${period === p ? ' active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p === 'alltime' ? 'All Time' : p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>

        {userRank && <div className="user-rank">Your rank: #{userRank}</div>}

        <table className="leaderboard-table">
          <thead>
            <tr><th>Rank</th><th>Player</th><th>Score</th></tr>
          </thead>
          <tbody>
            {leagueScores.length === 0 ? (
              <tr><td colSpan={3} className="leaderboard-empty">No scores yet</td></tr>
            ) : leagueScores.map((entry, index) => (
              <tr key={entry.id} className={entry.user_id === user.id ? 'user-row' : ''}>
                <td>#{index + 1}</td>
                <td>{entry.username || entry.email}</td>
                <td>{entry.score}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <button onClick={onBack} className="btn btn-play">Play Again</button>
      </div>
    );
  }

  return null;
}
